import React, { useEffect, useRef, useState } from 'react';
import HotelAutocompleteField from '@app/component/form/input/autocomplete/HotelAutocompleteField';
import FormButton from '@app/component/form/button/FormButton';
import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import usePropertiesState from '@app/site/registration/fragment/state/PropertiesState';
import { useForm } from 'react-hook-form';
import SearchHotelResult from '@app/component/form/input/autocomplete/SearchHotelResult';
import _isEmpty from 'lodash/isEmpty';
import Property from '@app/site/registration/fragment/state/Property';
import _clone from 'lodash/clone';
import _uniq from 'lodash/uniq';
import Message from '@app/component/message/Message';
import ModalHandler from '@app/component/overlays/modal/ModalHandler';
import _pull from 'lodash/pull';
import { messagesEn } from '@app/component/translation/i8n/messages-en';
import { useTranslation } from '@app/component/translation/Translation';
import FormInputText from '@app/component/form/input/text/FormInputText';
import useSiteState from '@app/state/SiteState';
import SetupPropertyHotelCompetitorSuggestionsModal
	from '@app/site/registration/overlay/SetupPropertyHotelCompetitorSuggestionsModal';

const SetupPropertyFormFragment: React.FunctionComponent<{
	index: number,
	onComplete: (properties: Property[]) => any,
	onChange: (property: Property | null) => any
}> = ({ index, onComplete, onChange }) => {

	const state = usePropertiesState();
	const { t } = useTranslation();

	const form = useForm<{ hotelId: number, webhotelierId: string | undefined, competitorIds: (number | undefined)[] }>();
	const [property, setProperty] = useState<Property | null>(null);
	const [globalError, setGlobalError] = useState<string>();
	const modal = useRef<ModalHandler>(null);
	const maxNumberOfCompetitors = 12;

	const principal = useSiteState(state => state.principal);

	useEffect(() => {
		form.reset({
			hotelId: state.properties[index].hotel?.id,
			webhotelierId: state.properties[index].webhotelierId,
			// @ts-ignore
			competitorIds: state.properties[index].competitors.filter(c => !!c).map(c => c.id) || [],
		});
		setProperty({
			hotel: state.properties[index].hotel,
			webhotelierId: state.properties[index].webhotelierId,
			competitors: state.properties[index].competitors,
		});
	}, [index]);

	useEffect(() => {
		openSuggestions();
	}, [property?.hotel]);

	const addNewCompetitor = () => {
		form.setValue('competitorIds', [...form.getValues('competitorIds'), undefined]);
		const copy = _clone(property);
		copy!.competitors = [...copy!.competitors!, undefined];
		setProperty(copy);
		onChange(copy!);
	};

	const removeCompetitor = (index: number) => {
		form.setValue('competitorIds', [...form.getValues('competitorIds')!.slice(0, index), ...form.getValues('competitorIds')!.slice(index + 1)]);
		const copy = _clone(property);
		copy!.competitors = [...copy!.competitors!.slice(0, index), ...copy!.competitors!.slice(index + 1)];
		setProperty(copy);
		onChange(copy!);
	};

	const setHotel = (hotel: SearchHotelResult) => {
		const copy = _clone(property);
		if (!!copy) {
			copy!.hotel = hotel;
			setProperty(copy);
			onChange(copy!);
		}
	};

	const setCompetitor = (idx: number, competitor: SearchHotelResult | undefined) => {
		if (!!competitor) {
			const copy = _clone(property);
			copy!.competitors[idx] = competitor;
			setProperty(copy);
			onChange(copy!);
		}
	};

	const openSuggestions = () => {
		if (!!property && !!modal.current && property.hotel) {
			modal.current.open(property.hotel.id);
		}
	};

	const onSave = (data: { hotelId: number, webhotelierId: string, competitorIds: (number | undefined)[] }) => {
		if (!data.hotelId) form.setError('hotelId', { message: 'This field is required!' });
		if (principal!.isFromWebhotelier && !data.webhotelierId) form.setError('webhotelierId', { message: 'This field is required!' });
		else if (_isEmpty(data.competitorIds)) setGlobalError('Please add at least one competitor');
		else if (data.competitorIds.includes(data.hotelId)) {
			form.setError(`hotelId`, { message: 'This property cannot be added as competitor' });
		} else if (data.competitorIds.includes(undefined)) {
			data.competitorIds.forEach((cId, idx) => {
				if (!cId) {
					form.setError(`competitorIds.${idx}`, { message: 'This field is required!' });
				}
			});
		} else if (_uniq(data.competitorIds).length !== data.competitorIds.length) {
			setGlobalError('There are duplicate competitors!');
		} else if (_uniq(data.competitorIds).length > maxNumberOfCompetitors) {
			setGlobalError(t(messagesEn.server.exception.registration.MAX_NUMBER_OF_COMPETITORS_SURPASSED));
		} else {
			const idx = state.properties.findIndex((p, i) => i !== index && !!p.hotel && p.hotel.id === data.hotelId);
			if (idx > -1) {
				form.setError(`hotelId`, { message: 'You have already added this property' });
			} else {
				onComplete(state.setProperty(index, {...property!, webhotelierId: data.webhotelierId}));
				onChange(null);
			}
		}
	};

	const onCancel = (data: { hotelId: number, competitorIds: (number | undefined)[] }) => {
		if (!data.hotelId || _isEmpty(data.competitorIds.filter(c => !!c))) {
			onComplete(state.removeProperty(index));
		} else
			onComplete(state.properties);
		onChange(null);
	};

	const onAddCompetitors = (competitors: SearchHotelResult[]) => {
		const copy = _clone(property);
		if (!!copy) {
			for (const competitor of competitors) {
				if (copy.competitors.findIndex(c => !!c && c.id === competitor.id) === -1) {
					copy.competitors = [competitor, ...copy.competitors];
				}
			}
			if (copy.competitors.length > 1) {
				_pull(copy.competitors, undefined);
			}
			form.setValue('competitorIds', copy.competitors.map(c => c!.id));
			setProperty(copy);
			onChange(copy);
		}
	};

	return <>
		<li className="gap-x-6 py-6">
			<div className="bg-gray-50 shadow-sm ring-1 ring-gray-900/10 rounded-xl md:col-span-2">
				<div className="gap-x-6 border-b border-gray-900/10 px-4 py-5 sm:px-8">
					<h3 className="text-base font-semibold leading-7 text-gray-900">
						Add your property and it's competitors
						<span
							className="ml-2 leading-6 text-sm text-gray-400 italic">(up to {maxNumberOfCompetitors} competitors)</span>
					</h3>
				</div>
				<div className="p-4 sm:p-8">
					<div>
						{globalError &&
							<Message state="error"
											 extraClassName="mb-6">{globalError}</Message>}
						<div>
							<div className="flex justify-between">
								<label className="label">
									Your Property
								</label>
							</div>
							<div className="mt-0.5">
								<HotelAutocompleteField placeholder="Insert property name" form={form}
																				name="hotelId"
																				onChange={setHotel} />
							</div>
							{principal!.isFromWebhotelier && <div className="mt-4">
								<div className="flex shadow-sm">
									<div
										className="flex shrink-0 items-center rounded-l-md bg-white px-3 text-base text-gray-600 outline outline-1 -outline-offset-1 outline-gray-300 text-sm/6 shadow-sm">
										Webhotelier ID
									</div>
									<FormInputText name="webhotelierId"
																 form={form}
																 extraClassName="-ml-px rounded-l-none" />
								</div>
							</div>}
						</div>
						{!!property?.hotel && <div className="mt-10">
							<div className="relative">
								<div className="absolute inset-0 flex items-center" aria-hidden="true">
									<div className="w-full border-t border-gray-300"></div>
								</div>
								<div className="relative flex items-center justify-between"><span
									className="bg-gray-50 pr-3 text-base font-semibold leading-6 text-gray-900">Competitors</span>
									<button type="button" onClick={openSuggestions}
													className="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
										<PlusIcon className="-ml-1 -mr-0.5 h-5 w-5 text-gray-400" />
										<span>See Suggestions</span>
									</button>
								</div>
							</div>
							<div className="mt-2">
								<ul role="list" className="divide-y divide-gray-200">
									{!!property && property.competitors && property.competitors.map((competitor, idx) => {
										return !!competitor ?
											<li className="flex justify-between gap-x-6 py-4" key={`competitor_${idx}`}>
												<div className="flex flex-col">
													<span className="text-sm font-medium text-gray-700">{competitor.name}</span>
												</div>
												<div>
													<FormButton state="white" onClick={() => removeCompetitor(idx)}
																			extraClassName="px-3">
														<XMarkIcon className="w-5 h-5 text-rose-400" />
													</FormButton>
												</div>
											</li> : <li className="py-4" key={`competitor_${idx}`}>
												<div>
													<div className="flex justify-between">
														<label htmlFor="competitor" className="label">
															Add Competitor
														</label>

													</div>
													<div className="flex space-x-4">
														<HotelAutocompleteField placeholder="Insert competitor"
																										form={form}
																										name={`competitorIds.${idx}`}
																										onChange={(competitor) => setCompetitor(idx, competitor)} />
														<div>
															<FormButton state="white"
																					onClick={() => removeCompetitor(idx)}
																					extraClassName="px-3">
																<XMarkIcon className="w-5 h-5 text-rose-400" />
															</FormButton>
														</div>
													</div>
												</div>
											</li>;
									})}
								</ul>
							</div>

							<div className="flex mt-2 pt-2 border-t border-gray-200">
								<FormButton state="text" onClick={addNewCompetitor}
														className="text-sm font-semibold leading-6 text-blue-600 hover:text-blue-500"><span
									aria-hidden="true" className="mr-1">+</span>Add another competitor
								</FormButton>
							</div>
						</div>}
					</div>
				</div>
				<div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
					{state.properties.length > 1 &&
						<FormButton state="text" onClick={form.handleSubmit(onCancel)}>Cancel</FormButton>}
					<FormButton state="primary" onClick={form.handleSubmit(onSave)}>Next</FormButton>
				</div>
			</div>
		</li>
		<SetupPropertyHotelCompetitorSuggestionsModal ref={modal} onSave={onAddCompetitors} />
	</>;
};

export default SetupPropertyFormFragment;